import {Link, Navigate, useLocation, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {FormTextField} from '../../ui/forms/input-field/text-field/text-field';
import {Button} from '../../ui/buttons/button';
import {Form} from '../../ui/forms/form';
import {LinkStyle} from '../../ui/buttons/external-link';
import {RegisterPayload, useRegister} from '../requests/use-register';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '../../i18n/trans';
import {FormCheckbox} from '../../ui/forms/toggle/checkbox';
import {CustomMenuItem} from '../../menus/custom-menu';
import {useRecaptcha} from '../../recaptcha/use-recaptcha';
import {StaticPageTitle} from '../../seo/static-page-title';
import {useSettings} from '../../core/settings/use-settings';
import {useContext} from 'react';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {MailIcon} from '@common/icons/material/Mail';

export function RegisterPage() {
  const {
    branding,
    registration: {disable},
  } = useSettings();
  const {auth} = useContext(SiteConfigContext);
  const {verify, isVerifying} = useRecaptcha('register');

  const {pathname} = useLocation();
  const [searchParams] = useSearchParams();

  const isWorkspaceRegister = pathname.includes('workspace');
  const isBillingRegister = searchParams.get('redirectFrom') === 'pricing';
  const searchParamsEmail = searchParams.get('email') || undefined;

  const splashVideo = searchParams.get('splash_video');
  const redirect = searchParams.get('redirect');
  const loginUrl = new URLSearchParams();
  if (splashVideo) loginUrl.set('splash_video', splashVideo);
  if (redirect) loginUrl.set('redirect', redirect);
  const loginQuery = loginUrl.toString();

  const form = useForm<RegisterPayload>({
    defaultValues: {email: searchParamsEmail},
  });
  const register = useRegister(form);

  if (disable) {
    return <Navigate to="/login" replace />;
  }

  let heading: React.ReactNode = (
    <div className="mb-20 flex w-full items-center justify-between border-b border-white/20 pb-12">
      <span className="text-xl font-bold text-white">
        <Trans message="SIGNUP" />
      </span>
      <Link
        className="text-xl text-primary-light hover:underline"
        to={loginQuery ? `/login?${loginQuery}` : '/login'}
      >
        <Trans message="LOGIN" />
      </Link>
    </div>
  );
  if (isWorkspaceRegister) {
    heading = (
      <Trans
        values={{siteName: branding?.site_name}}
        message="To join your team on :siteName, create an account"
      />
    );
  } else if (isBillingRegister) {
    heading = <Trans message="First, let's create your account" />;
  }

  return (
    <AuthLayout heading={heading}>
      <StaticPageTitle>
        <Trans message="Register" />
      </StaticPageTitle>
      <Form
        form={form}
        onSubmit={async payload => {
          const isValid = await verify();
          if (isValid) {
            register.mutate(payload);
          }
        }}
      >
        <FormTextField
          className=""
          name="email"
          type="email"
          disabled={!!searchParamsEmail}
          placeholder="Enter email"
          required
        />
        <FormTextField
          className=""
          name="password"
          type="password"
          placeholder="Enter password"
          required
        />
        <FormTextField
          className="mb-8"
          name="password_confirmation"
          type="password"
          placeholder="Confirm password"
          required
        />
        {auth?.registerFields ? <auth.registerFields /> : null}
        <PolicyCheckboxes />
        <Button
          className="block w-full"
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          disabled={register.isPending || isVerifying}
          startIcon={<MailIcon />}
        >
          <Trans message="Sign up with Email" />
        </Button>
      </Form>
    </AuthLayout>
  );
}

function PolicyCheckboxes() {
  const {
    registration: {policies},
  } = useSettings();

  if (!policies) return null;

  return (
    <div className="mb-16">
      {policies.map(policy => (
        <FormCheckbox
          key={policy.id}
          name={policy.id}
          className="mb-4 block"
          required
        >
          <Trans
            message="I accept the :name"
            values={{
              name: (
                <CustomMenuItem
                  unstyled
                  className={() => LinkStyle}
                  item={policy}
                />
              ),
            }}
          />
        </FormCheckbox>
      ))}
    </div>
  );
}
